
import { Component, Emit, namespace, Prop, Vue } from 'nuxt-property-decorator'
import difference from 'lodash/difference'
import { filtersList as commonFiltersList } from '~/core/config/filters/amp/common.config'
import { filtersList as mobileFiltersList } from '~/core/config/filters/amp/mobile.config'
import { filtersList as homeFiltersList } from '~/core/config/filters/amp/home.config'
import {
  CommonFiltersState,
  FilterChangeEventPayload,
  FiltersItem,
  FiltersItemValue,
  FiltersList,
  HomeFiltersState,
  MobileFiltersState,
} from '~/core/config/filters/types'
import { namespace as AmpCommonFiltersModuleNamespace } from '~/store/modules/Amp/Filters/Common'
import { namespace as AmpMobileFiltersModuleNamespace } from '~/store/modules/Amp/Filters/Mobile'
import { namespace as AmpHomeFiltersModuleNamespace } from '~/store/modules/Amp/Filters/Home'
import IconFilter from '~/components/icons/IconFilter.vue'
import OfferModal from '~/components/filters/OfferModal.vue'
import BottomSheet from '~/components/common/BottomSheet.vue'
import { OfferTypeByShortName, OfferTypeKey } from '~/core/offer.types'
import IconFilter2 from '~/components/icons/IconFilter2.vue'
import AddressFilter from '~/components/filters/AddressFilter/AddressFilter.vue'
import { addressObservable } from '~/observables/modules/address'
import FilterButton from '~/components/comparator/filters/FilterButton.vue'
import { OnCreated } from '~/core/vue.types'
import { AlButton } from '~/components/ui'

const TileSelection = () => ({
  component: import(/* webpackChunkName: "TileSelection" */ '~/components/comparator/filters/TileSelection.vue'),
  timeout: 3000,
})
const MultipleSelection = () => ({
  component: import(/* webpackChunkName: "MultipleSelection" */ '~/components/comparator/filters/MultipleSelection.vue'),
  timeout: 3000,
})
const SmallMultipleSelection = () => ({
  component: import(/* webpackChunkName: "SmallMultipleSelection" */ '~/components/comparator/filters/SmallMultipleSelection.vue'),
  timeout: 3000,
})
const SingleTileSelection = () => ({
  component: import(/* webpackChunkName: "SingleTileSelection" */ '~/components/comparator/filters/SingleTileSelection.vue'),
  timeout: 3000,
})
const ComparatorSwitch = () => ({
  component: import(/* webpackChunkName: "SingleTileSelection" */ '~/components/comparator/filters/ComparatorSwitch.vue'),
  timeout: 3000,
})

const commonFiltersModuleNs = namespace(AmpCommonFiltersModuleNamespace)
const homeFiltersModuleNs = namespace(AmpHomeFiltersModuleNamespace)
const mobileFiltersModuleNs = namespace(AmpMobileFiltersModuleNamespace)

enum TrackPlanCategory {
  mobile = 'mobile',
  home = 'home',
  bundles = 'bundles',
}

enum TrackEventActionPrepend {
  mobile = 'Mobile',
  home = 'Home',
}

@Component({
  components: {

    IconFilter2,
    IconFilter,
    OfferModal,
    BottomSheet,
    FilterButton,
    AddressFilter,
    AlButton,
  },
})
export default class ComparatorFilter extends Vue implements OnCreated {
  @Prop({
    required: true,
    type: String,
  })
  protected selectedOfferType!: OfferTypeKey

  @Prop({ type: String })
  readonly providerId?: string

  @Prop({ type: String })
  readonly providerSlug?: string

  @commonFiltersModuleNs.Getter('GET_FILTERS')
  readonly getCommonFilters!: CommonFiltersState

  @commonFiltersModuleNs.Mutation('SET_FILTER')
  setCommonFilters!: ({
    value,
    key,
  }: { value: FiltersItemValue, key: string }) => void

  @commonFiltersModuleNs.Action('RESET_FILTER')
  resetCommonFilters!: () => void

  @commonFiltersModuleNs.Getter('IS_FILTERS_AT_INITIAL_STATE')
  readonly isCommonFiltersAtInitialState!: boolean

  @mobileFiltersModuleNs.Getter('GET_FILTERS')
  readonly getMobileFilters!: MobileFiltersState

  @mobileFiltersModuleNs.Mutation('SET_FILTER')
  setMobileFilters!: ({
    value,
    key,
  }: { value: FiltersItemValue, key: string }) => void

  @mobileFiltersModuleNs.Action('RESET_FILTER')
  resetMobileFilters!: () => void

  @homeFiltersModuleNs.Getter('GET_FILTERS')
  readonly getHomeFilters!: HomeFiltersState

  @mobileFiltersModuleNs.Getter('IS_FILTERS_AT_INITIAL_STATE')
  readonly isMobileFiltersAtInitialState!: boolean

  @homeFiltersModuleNs.Getter('IS_FILTERS_AT_INITIAL_STATE')
  readonly isHomeFiltersAtInitialState!: boolean

  @homeFiltersModuleNs.Mutation('SET_FILTER')
  setHomeFilters!: ({
    value,
    key,
  }: { value: FiltersItemValue, key: string }) => void

  @homeFiltersModuleNs.Action('RESET_FILTER')
  resetHomeFilters!: () => void

  private commonFilters: FiltersList = commonFiltersList(this.$store.getters)
  private mobileFilters: FiltersList = mobileFiltersList(this.$store.getters)
  private homeFilters: FiltersList = homeFiltersList(this.$store.getters)
  private isMobileAdvanced = false
  private isHomeAdvanced = false
  private isModalFilterShow = false
  private TrackPlanCategory = TrackPlanCategory
  private TrackEventActionPrepend = TrackEventActionPrepend

  get isResetButtonDisabled (): boolean {
    if (this.isMobile) {
      return this.isMobileFiltersAtInitialState && this.isCommonFiltersAtInitialState
    }

    if (this.isHome) {
      return this.isHomeFiltersAtInitialState && this.isCommonFiltersAtInitialState
    }

    if (this.isBundle) {
      return this.isMobileFiltersAtInitialState && this.isHomeFiltersAtInitialState && this.isCommonFiltersAtInitialState
    }

    return false
  }

  get isAddressRequired () {
    return addressObservable.zip.length !== 0
  }

  get randomId () {
    return 'random?'
  }

  get isMobile () {
    return this.selectedOfferType === OfferTypeByShortName.mobile
  }

  get isHome () {
    return this.selectedOfferType === OfferTypeByShortName.home
  }

  get isBundle () {
    return this.selectedOfferType === OfferTypeByShortName.bundle
  }

  private components = {
    TileSelection,
    MultipleSelection,
    SmallMultipleSelection,
    SingleTileSelection,
    ComparatorSwitch,
  }

  @Emit('filter:change')
  onCommonFiltersChange (value: FiltersItemValue, filter: FiltersItem): FilterChangeEventPayload {
    this.trackFiltersChanging(
      this.isBundle ? TrackPlanCategory.bundles : TrackPlanCategory.mobile,
      filter,
      value,
      this.getCommonFilters[filter.name as keyof CommonFiltersState],
      this.isBundle ? TrackEventActionPrepend.mobile : '',
    )
    this.setCommonFilters({ value, key: filter.name })

    return {
      key: filter.name,
      value,
    }
  }

  @Emit('filter:change')
  onMobileFiltersChange (value: FiltersItemValue, filter: FiltersItem): FilterChangeEventPayload {
    this.trackFiltersChanging(
      this.isBundle ? TrackPlanCategory.bundles : TrackPlanCategory.mobile,
      filter,
      value,
      this.getMobileFilters[filter.name as keyof MobileFiltersState],
      this.isBundle ? TrackEventActionPrepend.mobile : '',
    )
    this.setMobileFilters({ value, key: filter.name })

    return {
      key: filter.name,
      value,
    }
  }

  @Emit('filter:change')
  onHomeFiltersChange (value: FiltersItemValue, filter: FiltersItem): FilterChangeEventPayload {
    this.trackFiltersChanging(
      this.isBundle ? TrackPlanCategory.bundles : TrackPlanCategory.home,
      filter,
      value,
      this.getHomeFilters[filter.name as keyof HomeFiltersState],
      this.isBundle ? TrackEventActionPrepend.home : '',
    )
    this.setHomeFilters({ value, key: filter.name })

    return {
      key: filter.name,
      value,
    }
  }

  onMobileFiltersExpanderClick (value: boolean, filter: FiltersItem) {
    this.trackExpandFilters(
      this.isBundle ? TrackPlanCategory.bundles : TrackPlanCategory.mobile,
      filter,
      value,
      this.isBundle ? TrackEventActionPrepend.mobile : '',
    )
  }

  onHomeFiltersExpanderClick (value: boolean, filter: FiltersItem) {
    this.trackExpandFilters(
      this.isBundle ? TrackPlanCategory.bundles : TrackPlanCategory.home,
      filter,
      value,
      this.isBundle ? TrackEventActionPrepend.home : '',
    )
  }

  onHomeCityChange (value: string) {
    this.trackFiltersChanging(
      this.isBundle ? TrackPlanCategory.bundles : TrackPlanCategory.home,
      { name: 'city', component: '', props: {} },
      value,
      '',
      this.isBundle ? TrackEventActionPrepend.home : '',
    )
  }

  onHomeStreetChange (value: string) {
    this.trackFiltersChanging(
      this.isBundle ? TrackPlanCategory.bundles : TrackPlanCategory.home,
      { name: 'street', component: '', props: {} },
      value,
      '',
      this.isBundle ? TrackEventActionPrepend.home : '',
    )
  }

  onHomeNumberChange (value: string) {
    this.trackFiltersChanging(
      this.isBundle ? TrackPlanCategory.bundles : TrackPlanCategory.home,
      { name: 'house_number', component: '', props: {} },
      !!value, // No value is passed to protect user privacy
      '',
      this.isBundle ? TrackEventActionPrepend.home : '',
    )
  }

  onMobileAdvancedClick () {
    this.isMobileAdvanced = true

    this.trackFiltersChanging(
      this.isBundle ? TrackPlanCategory.bundles : TrackPlanCategory.mobile,
      { name: '', component: '', props: { title: 'offerFilters.advancedFilter' } },
      this.isMobileAdvanced,
      !this.isMobileAdvanced,
      this.isBundle ? TrackEventActionPrepend.mobile : '',
    )
  }

  onHomeAdvancedClick () {
    this.isHomeAdvanced = true

    this.trackFiltersChanging(
      this.isBundle ? TrackPlanCategory.bundles : TrackPlanCategory.home,
      { name: '', component: '', props: { title: 'offerFilters.advancedFilter' } },
      this.isHomeAdvanced,
      !this.isHomeAdvanced,
      this.isBundle ? TrackEventActionPrepend.home : '',
    )
  }

  getTrackEventLabel (force: boolean, value: FiltersItemValue) {
    return `${force ? 'select' : 'unselect'}${typeof value === 'boolean' || !value.length ? '' : '|' + value}`
  }

  trackFiltersChanging (planCategory: TrackPlanCategory, filter: FiltersItem, newValue: FiltersItemValue, oldValue: FiltersItemValue, eventActionPrepend = '') {
    let valueForce = Boolean(newValue)
    let valueDiff = Array.isArray(newValue) && Array.isArray(oldValue) ? difference(newValue, oldValue) : newValue
    let valueDiffEn

    if (Array.isArray(valueDiff) && !valueDiff.length) {
      valueForce = false
      valueDiff = Array.isArray(newValue) && Array.isArray(oldValue) ? difference(oldValue, newValue) : newValue
    }

    if (Array.isArray(valueDiff)) {
      valueDiffEn = valueDiff.map(value => this.$t(filter.props.items?.find(el => el.value === value)?.name || '', 'en') as string || value)
    } else {
      valueDiffEn = this.$t(filter.props.items?.find(el => el.value === valueDiff)?.name || '', 'en') as string || valueDiff
    }

    this.$analytics.GTM.trackRaw({
      event: 'GTMevent',
      eventCategory: `filter_${planCategory}`,
      eventAction: `${eventActionPrepend ? eventActionPrepend + '|' : ''}${this.$t(filter.props.subTitle || filter.props.title || '', 'en') || filter.name}`,
      eventLabel: this.getTrackEventLabel(valueForce, valueDiffEn),
      language_code: this.$i18n.locale.toUpperCase(),
    })
  }

  trackExpandFilters (planCategory: TrackPlanCategory, filter: FiltersItem, value: boolean, eventActionPrepend = '') {
    this.$analytics.GTM.trackRaw({
      event: 'GTMevent',
      eventCategory: `filter_${planCategory}`,
      eventAction: `${eventActionPrepend ? eventActionPrepend + '|' : ''}${this.$t(filter.props.title || '', 'en')}`,
      eventLabel: `Show ${value ? 'more' : 'less'}`,
      language_code: this.$i18n.locale.toUpperCase(),
    })
  }

  resetFilters () {
    this.resetCommonFilters()

    if (this.isMobile) {
      this.resetMobileFilters()
    } else if (this.isHome) {
      this.resetHomeFilters()
    } else {
      this.resetMobileFilters()
      this.resetHomeFilters()
    }
  }

  @Emit('filter:reset')
  onResetBtnClick () {
    this.resetFilters()

    let eventCategory: string

    if (this.isMobile) {
      eventCategory = 'filter_mobile'
    } else if (this.isHome) {
      eventCategory = 'filter_home'
    } else {
      eventCategory = 'filter_bundles'
    }

    this.$analytics.GTM.trackRaw({
      event: 'GTMevent',
      eventCategory,
      eventAction: 'reset',
      eventLabel: '',
      language_code: this.$i18n.locale.toUpperCase(),
    })
  }

  get isMobileWrapper () {
    return (this.$mqUtils.isMobile || this.$mqUtils.isLG || this.$mqUtils.isMD)
  }

  get dynamicSlotName () {
    return this.isMobileWrapper ? 'content' : ''
  }

  get wrapperComponent () {
    return this.isMobileWrapper ? BottomSheet : 'div'
  }

  created () {
    const getPreparedFilters = () => {
      const commonFilters: FiltersList = commonFiltersList(this.$store.getters)

      if (this.isMobile) {
        const mobileFilters: FiltersList = mobileFiltersList(this.$store.getters)
        return [...commonFilters, ...mobileFilters].map((f) => {
          return {
            name: f.name,
            items: f.props.items?.map(i => i.value),
          }
        })
      }
      if (this.isHome) {
        const homeFilters: FiltersList = homeFiltersList(this.$store.getters)
        return [...commonFilters, ...homeFilters].map((f) => {
          return {
            name: f.name,
            items: f.props.items?.map(i => i.value),
          }
        })
      }
    }

    const getPreparedGetParams = () => {
      const result = []
      let key
      for (key in this.$route.query) {
        result.push({
          name: key,
          items: this.$route.query[key],
        })
      }
      return result
    }

    const matchFilters = () => {
      const filters = getPreparedFilters()
      let params = getPreparedGetParams()
      if (!filters?.length || !params?.length) {
        return
      }

      // remove bad get params
      params = params.filter((p) => {
        return filters.find(f => p.name === f.name)
      })

      // get params to array
      let finalParams = params.map((p) => {
        // for ?&special_offers=under_30&special_offers=under_40
        if (Array.isArray(p.items)) {
          return {
            ...p,
            items: [...p.items.join(',').split(',')],
          }
        } else {
          return {
            ...p,
            items: [...p.items.split(',')],
          }
        }
      })

      // remove bad values
      finalParams = finalParams.map((p) => {
        return {
          ...p,
          items: p.items.filter((i: string | boolean) => filters.find(f => f.name === p.name)?.items?.includes(i)),
        }
      })
      return finalParams
    }

    const setFilterByParams = (setFunction: any) => {
      const filters = matchFilters()
      filters?.forEach((f) => {
        setFunction({
          value: f.items,
          key: f.name,
        })
      })
    }

    // set filters
    if (matchFilters()?.length) {
      try {
        this.resetFilters()

        setFilterByParams(this.setCommonFilters)

        if (this.isMobile) {
          setFilterByParams(this.setMobileFilters)
        } else if (this.isHome) {
          setFilterByParams(this.setHomeFilters)
        }
      } catch (e) {

      }
    }

    if (!this.providerSlug) {
      return
    }

    if (this.isMobile || this.isBundle) {
      this.setMobileFilters({
        value: [this.providerSlug],
        key: 'providers',
      })
    }
    if (this.isHome || this.isBundle) {
      this.setHomeFilters({
        value: [this.providerSlug],
        key: 'providers',
      })
    }
  }

  setIsModalFilterShow (value: boolean) {
    this.isModalFilterShow = value
  }
}
